import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TokenRawHandler } from 'src/app/auth/handler/token-raw.handler';
import { EndPointService } from 'src/app/core/services/end-point.service';
import { EmployeeService } from 'src/app/shared/personnel-files/services/employee.service';
import { DevelopmentObjective, DevelopmentObjectiveComment } from '../../development-objectives/models/DevelopmentObjective';
import { ICompanyObjective } from '../models/CompanyObjective';
import { CompanyObjectiveAttachment } from '../models/CompanyObjectiveAttachment';
import { CompanyObjectiveFull } from '../models/CompanyObjectiveFull';
import { CompanyObjectiveGroupInformation } from '../models/CompanyObjectiveGroupInformation';
import { ICompanyObjectiveSettings } from '../models/CompanyObjectiveSettings';
import { EmployeeObjectiveStatistics } from '../models/EmployeeObjectiveStatistics';
import { IObjective, IObjectiveListResponse, ObjectiveComment, ObjectiveStatus } from '../models/Objective';
import { ObjectiveAttachment } from '../models/ObjectiveAttachement';
import { ObjectiveCategory } from '../models/ObjectiveCategory';
import { ObjectiveDomain } from '../models/ObjectiveDomain';
import { ObjectiveRights } from '../models/ObjectiveRights';
import { IObjectiveSettings } from '../models/ObjectiveSettings';
import { ObjectiveType } from '../models/ObjectiveType';
import { PerformanceObjective } from '../models/PerformanceObjective';
import { ReviewPeriod } from '../models/ReviewPeriod';

@Injectable({
  providedIn: 'root'
})
export class ObjectiveService {
  private _baseUrl = '/api/reviews/objective/';
  private httpRaw: HttpClient;

  constructor(private _endPoints: EndPointService, private _employeeService: EmployeeService, private http: HttpClient, private handler: HttpBackend) {
    this.registerEndPoints();
    this.httpRaw = new HttpClient(new TokenRawHandler(handler));
  }

  private registerEndPoints(): void {
    this._endPoints.registerEndPoint<IObjectiveListResponse>('objective_get_list', `${this._baseUrl}list`, true);
    this._endPoints.registerEndPoint<IObjective[]>('objective_get_linkable', `${this._baseUrl}linkable`, true);
    this._endPoints.registerEndPoint<void>('objective_link', `${this._baseUrl}link`, true);
    this._endPoints.registerEndPoint<ICompanyObjectiveSettings>('objective_get_settings', `${this._baseUrl}settings`, true);
    this._endPoints.registerEndPoint<IObjectiveSettings>('objective_get_all_settings', `${this._baseUrl}settings`, false);
    this._endPoints.registerEndPoint<ICompanyObjectiveSettings>('objective_get_all_companysettings', `${this._baseUrl}companysettings`, false);
    this._endPoints.registerEndPoint<EmployeeObjectiveStatistics[]>('objective_get_employeestatistics', `${this._baseUrl}employeestatistics`, false);

    this._endPoints.registerEndPoint<void>('objective_save_settings', `${this._baseUrl}savesettings`, false);
    this._endPoints.registerEndPoint<void>('objective_save_performance_settings', `${this._baseUrl}saveperformancesettings`, false);
    this._endPoints.registerEndPoint<void>('objective_save_company_settings', `${this._baseUrl}savecompanyesettings`, false);

    this._endPoints.registerEndPoint<void>('devobjective_save_rights', `${this._baseUrl}savedevrights`, false);
    this._endPoints.registerEndPoint<void>('performanceobjective_save_rights', `${this._baseUrl}saveperformancerights`, false);

    this._endPoints.registerEndPoint<ObjectiveStatus[]>('objective_get_statusses', `${this._baseUrl}status`, true);
    this._endPoints.registerEndPoint<ObjectiveCategory[]>('objective_get_categories', `${this._baseUrl}category`, true);
    this._endPoints.registerEndPoint<ObjectiveType[]>('objective_get_types', `${this._baseUrl}type`, true);
    this._endPoints.registerEndPoint<ObjectiveDomain[]>('objective_get_domains', `${this._baseUrl}domain`, true);

    this._endPoints.registerEndPoint<ObjectiveComment[]>('objective_get_comments', `${this._baseUrl}comments`, false);
    this._endPoints.registerEndPoint<number>('objective_add_comment', `${this._baseUrl}addcomment`, false);
    this._endPoints.registerEndPoint<void>('objective_update_comment', `${this._baseUrl}updatecomment`, false);
    this._endPoints.registerEndPoint<void>('objective_delete_comment', `${this._baseUrl}deletecomment`, false);

    this._endPoints.registerEndPoint<DevelopmentObjective[]>('devobjective_get', `${this._baseUrl}devgoals`, false);
    this._endPoints.registerEndPoint<number>('devobjective_add', `${this._baseUrl}adddevgoal`, false);
    this._endPoints.registerEndPoint<void>('devobjective_update', `${this._baseUrl}updatedevgoal`, false);
    this._endPoints.registerEndPoint<void>('devobjective_delete', `${this._baseUrl}deletedevgoal`, false);
    this._endPoints.registerEndPoint<DevelopmentObjectiveComment[]>('devobjective_get_comments', `${this._baseUrl}devcomments`, false);
    this._endPoints.registerEndPoint<number>('devobjective_add_comment', `${this._baseUrl}adddevcomment`, false);
    this._endPoints.registerEndPoint<void>('devobjective_delete_comment', `${this._baseUrl}deletedevcomment`, false);

    this._endPoints.registerEndPoint<void>('objective_get_performanceobjectives', `${this._baseUrl}getemployeeperformancegoals`, false);
    this._endPoints.registerEndPoint<void>('objective_get_previousobjectives', `${this._baseUrl}getpreviousgoals`, false);
    this._endPoints.registerEndPoint<void>('objective_get_deletedobjectives', `${this._baseUrl}getdeletedgoals`, false);

    this._endPoints.registerEndPoint<void>('objective_update_performanceobjective', `${this._baseUrl}updateemployeeperformancegoal`, false);
    this._endPoints.registerEndPoint<void>('objective_delete_performanceobjective', `${this._baseUrl}deleteemployeeperformancegoal`, false);
    this._endPoints.registerEndPoint<void>('objective_get_performanceobjectiveattachments', `${this._baseUrl}getperformanceobjectiveattachments`, false);
    this._endPoints.registerEndPoint<void>('objective_upload_performanceobjectiveattachments', `${this._baseUrl}uploadobjectiveattachtment`, false);
    this._endPoints.registerEndPoint<void>('objective_delete_performanceobjectiveattachments', `${this._baseUrl}deleteobjectiveattachtment`, false);

    this._endPoints.registerEndPoint<void>('objective_upload_companyobjectivesetting_allow', `${this._baseUrl}uploadsettingcompanyobjectiveallow`, false);
    this._endPoints.registerEndPoint<CompanyObjectiveFull>('objective_get_companyobjectives', `${this._baseUrl}getallcompanyobjectives`, false);
    this._endPoints.registerEndPoint<CompanyObjectiveFull>('objective_get_deletedcompanyobjectives', `${this._baseUrl}getdeletedcompanyobjectives`, false);
    this._endPoints.registerEndPoint<CompanyObjectiveFull>('objective_get_previouscompanyobjectives', `${this._baseUrl}getpreviouscompanyobjectives`, false);

    this._endPoints.registerEndPoint<CompanyObjectiveFull>('objective_add_companyobjective', `${this._baseUrl}addcompanyobjective`, false);
    this._endPoints.registerEndPoint<CompanyObjectiveAttachment>('objective_get_companyobjectiveattachments', `${this._baseUrl}getcompanyobjectiveattachments`, false);
    this._endPoints.registerEndPoint<void>('objective_update_companyobjective', `${this._baseUrl}updatecompanyobjective`, false);
    this._endPoints.registerEndPoint<CompanyObjectiveGroupInformation>('objective_get_companyobjectiveinformation', `${this._baseUrl}getcompanyobjectiveinformation`, false);
    this._endPoints.registerEndPoint<void>('objective_delete_companyobjective', `${this._baseUrl}deletecompanyobjective`, false);

    this._endPoints.registerEndPoint<void>('objective_upload_companyobjectiveattachments', `${this._baseUrl}uploadcompanyobjectiveattachtment`, false);
    this._endPoints.registerEndPoint<void>('objective_delete_companyobjectiveattachments', `${this._baseUrl}deletecompanyobjectiveattachtment`, false);

    this._endPoints.registerEndPoint<void>('objective_save_objective_category', `${this._baseUrl}saveobjectivecategory`, false);
    this._endPoints.registerEndPoint<void>('objective_save_new_category', `${this._baseUrl}savenewobjectivecategory`, false);
    this._endPoints.registerEndPoint<void>('objective_undo_deleted_category', `${this._baseUrl}undodeletedobjectivecategory`, false);
    this._endPoints.registerEndPoint<void>('objective_deleted_category', `${this._baseUrl}deletedobjectivecategory`, false);
    this._endPoints.registerEndPoint<void>('set_performanceobjectives_to_individualobjective', `${this._baseUrl}setperformanceobjectivestoindividualobjective`, false);
  }

  public getList(fetch?: boolean): Observable<IObjectiveListResponse> {
    return this._endPoints.call<IObjectiveListResponse>('objective_get_list', null, fetch);
  }

  public getLinkableObjectives(managerid: number, fetch?: boolean): Observable<IObjective[]> {
    return this._endPoints.call<IObjective[]>('objective_get_linkable', { studentId: managerid }, fetch);
  }

  public linkObjective(objectiveId: number, managerObjectiveId: number): Observable<void> {
    return this._endPoints.call<void>('objective_link', { objectiveId, managerObjectiveId });
  }

  public getSettings(studentId?: number, fetch?: boolean): Observable<ICompanyObjectiveSettings> {
    return this._endPoints.call<ICompanyObjectiveSettings>('objective_get_settings', { studentId: studentId || null }, fetch);
  }
  public getObjectiveRights(studentId: number): Observable<ObjectiveRights> {
    return this.http.post<ObjectiveRights>(`${this._baseUrl}getobjectiverights/${studentId}`, null);
  }
  public getAllSettings(studentId?: number, fetch?: boolean): Observable<IObjectiveSettings> {
    return this.http.post<IObjectiveSettings>(`${this._baseUrl}settings`, { studentId: studentId || null });
  }

  public getAllCompanySettings(): Observable<ICompanyObjectiveSettings> {
    return this._endPoints.call<ICompanyObjectiveSettings>('objective_get_all_companysettings', fetch);
  }
  public getEmployeeStatistics(studentId?: number, fetch?: boolean): Observable<EmployeeObjectiveStatistics[]> {
    return this.http.post<EmployeeObjectiveStatistics[]>(`${this._baseUrl}employeestatistics`, { studentId: studentId || null });
  }

  public saveSettings(settings: ICompanyObjectiveSettings): Observable<void> {
    return this._endPoints.call<void>('objective_save_settings', settings);
  }

  public savePerformanceSettings(settings: IObjectiveSettings): Observable<void> {
    return this._endPoints.call<void>('objective_save_performance_settings', settings);
  }

  public saveCompanySettings(settings: IObjectiveSettings, companySettings: ICompanyObjectiveSettings): Observable<void> {
    return this._endPoints.call<void>('objective_save_company_settings', { settings, companySettings });
  }

  public saveRights(allowDevelopmentGoalsCreate: boolean, allowDevelopmentGoalsEdit: boolean, allowDevelopmentGoalsDelete: boolean, studentId: number) {
    return this._endPoints.call<void>('devobjective_save_rights', { studentId, create: allowDevelopmentGoalsCreate, edit: allowDevelopmentGoalsEdit, delete: allowDevelopmentGoalsDelete });
  }

  public saveEmployeeRights(studentId: number, rights: ObjectiveRights) {
    return this._endPoints.call<void>('performanceobjective_save_rights', { studentId, ...rights });
  }

  public getStatusses(fetch?: boolean): Observable<ObjectiveStatus[]> {
    return this.http.post<ObjectiveStatus[]>(`${this._baseUrl}status`, null);
  }

  public getCategories(fetch?: boolean): Observable<ObjectiveCategory[]> {
    return this.http.post<ObjectiveCategory[]>(`${this._baseUrl}category`, null);
  }

  public getTypes(fetch?: boolean): Observable<ObjectiveType[]> {
    return this.http.post<ObjectiveType[]>(`${this._baseUrl}type`, null);
  }

  public getDomains(fetch?: boolean): Observable<ObjectiveDomain[]> {
    return this.http.post<ObjectiveDomain[]>(`${this._baseUrl}domain`, null);
  }

  // comments
  public getComments(objectiveId: number, forCompletedConversation?: number): Observable<ObjectiveComment[]> {
    return this._endPoints.call<ObjectiveComment[]>('objective_get_comments', { objectiveId, forCompletedConversation });
  }

  public addComment(comment: ObjectiveComment): Observable<number> {
    return this._endPoints.call<number>('objective_add_comment', comment);
  }

  public updateComment(comment: ObjectiveComment): Observable<void> {
    return this._endPoints.call<void>('objective_update_comment', comment);
  }

  public deleteComment(commentId: number): Observable<void> {
    return this._endPoints.call<void>('objective_delete_comment', { commentId });
  }

  // get company goals for performance objective
  public getCompanyGoals(studentId: number, forScoring?: boolean, forCompletedConversation?: number): Observable<ICompanyObjective[]> {
    return this.http.post<ICompanyObjective[]>(`${this._baseUrl}getcompanygoals` + (forScoring ? '?forScoring=true' : ''), { studentId, forCompletedConversation });
  }

  // employee performance goals
  public addEmployeePerformanceObjective(objective: PerformanceObjective, employeeId: number, forReviewYear = false): Observable<number> {
    return this.http.post<number>(`${this._baseUrl}addemployeeperformancegoals` + (forReviewYear ? "?forReviewYear=true" : ""), { objective, employeeId });
  }

  public getPreviousObjectives(employeeId: number): Observable<PerformanceObjective[]> {
    return this._endPoints.call<PerformanceObjective[]>('objective_get_previousobjectives', employeeId);
  }

  public getDeletedObjectives(employeeId: number): Observable<PerformanceObjective[]> {
    return this._endPoints.call<PerformanceObjective[]>('objective_get_deletedobjectives', employeeId);
  }

  public getEmployeePerformanceObjectives(studentId: number, forScoring?: boolean, forCompletedConversation?: number): Observable<PerformanceObjective[]> {
    return this.http.post<PerformanceObjective[]>(`${this._baseUrl}getemployeeperformancegoals` + (forScoring ? '?forScoring=true' : ''), { studentId, forCompletedConversation });
  }

  public updateEmployeePerformanceObjective(objective: PerformanceObjective): Observable<void> {
    return this._endPoints.call<void>('objective_update_performanceobjective', objective);
  }

  public deleteObjective(objectiveId: number): Observable<void> {
    return this._endPoints.call<void>('objective_delete_performanceobjective', objectiveId);
  }

  public getObjectiveAttachments(objectiveId: number, forCompletedConversation?: number): Observable<ObjectiveAttachment[]> {
    return this._endPoints.call<ObjectiveAttachment[]>('objective_get_performanceobjectiveattachments', { objectiveId, forCompletedConversation });
  }

  public uploadObjectiveAttachments(form: FormData): Observable<void> {
    return this.httpRaw.post<void>(
      `${this._baseUrl}uploadobjectiveattachtment`,
      form,
    );
  }

  public deleteObjectiveAttachment(objectiveAttachmentId: number): Observable<void> {
    return this._endPoints.call<void>('objective_delete_performanceobjectiveattachments', objectiveAttachmentId);
  }

  //company obejctive
  public saveAllowCompany(allowCompanyGoals: boolean): Observable<void> {
    return this._endPoints.call<void>('objective_upload_companyobjectivesetting_allow', allowCompanyGoals);
  }

  public addCompanyObjective(objective: CompanyObjectiveFull): Observable<number> {
    return this._endPoints.call<number>('objective_add_companyobjective', objective);
  }

  public getCompanyObjectiveAttachments(objectiveId: number, forCompletedConversation?: number): Observable<CompanyObjectiveAttachment[]> {
    return this._endPoints.call<CompanyObjectiveAttachment[]>('objective_get_companyobjectiveattachments', { objectiveId, forCompletedConversation });
  }

  public deleteCompanyObjectiveAttachment(objectiveAttachmentId: number): Observable<void[]> {
    return this._endPoints.call<void[]>('objective_delete_companyobjectiveattachments', objectiveAttachmentId);
  }

  public updateCompanyObjective(objective: CompanyObjectiveFull): Observable<void> {
    return this._endPoints.call<void>('objective_update_companyobjective', objective);
  }

  public getAllCompanyGoals(): Observable<CompanyObjectiveFull[]> {
    return this._endPoints.call<CompanyObjectiveFull[]>('objective_get_companyobjectives', null, true);
  }

  public getCurrentPeriod(forScoring?: boolean): Observable<ReviewPeriod> {
    return this.http.post<ReviewPeriod>(`${this._baseUrl}getcurrentperiod` + (forScoring ? '?forScoring=true' : ''), null);
  }

  public getGroupInformation(id: number): Observable<CompanyObjectiveGroupInformation[]> {
    return this._endPoints.call<CompanyObjectiveGroupInformation[]>('objective_get_companyobjectiveinformation', id, true);
  }

  public getDeletedCompanyGoals(): Observable<CompanyObjectiveFull[]> {
    return this._endPoints.call<CompanyObjectiveFull[]>('objective_get_deletedcompanyobjectives', null, true);
  }

  public getPreviousCompanyGoals(): Observable<CompanyObjectiveFull[]> {
    return this._endPoints.call<CompanyObjectiveFull[]>('objective_get_previouscompanyobjectives', null, true);
  }

  public deleteCompanyObjective(objectiveId: number): Observable<void> {
    return this._endPoints.call<void>('objective_delete_companyobjective', objectiveId);
  }

  public uploadCompanyObjectiveAttachments(form: FormData): Observable<void> {
    return this.httpRaw.post<void>(
      `${this._baseUrl}uploadcompanyobjectiveattachtment`,
      form,
    );
  }

  public saveObjectiveCategory(category: ObjectiveCategory): Observable<void> {
    return this._endPoints.call<void>('objective_save_objective_category', category);
  }

  public saveNewCategory(category: ObjectiveCategory): Observable<void> {
    return this._endPoints.call<void>('objective_save_new_category', category);
  }

  public unDeleteObjectiveCategory(category: ObjectiveCategory): Observable<void> {
    return this._endPoints.call<void>('objective_undo_deleted_category', category);
  }

  public deleteObjectiveCategory(category: ObjectiveCategory): Observable<void> {
    return this._endPoints.call<void>('objective_deleted_category', category);
  }

  public setPerformanceObjectivesToIndividualObjective(): Observable<void> {
    return this._endPoints.call<void>('set_performanceobjectives_to_individualobjective', null);
  }


}
